/**
 * Net30Panel
 * ----------
 * Self-contained panel that drives the "Activate as Net 30 Customer" flow
 * for a single health system.
 *
 * - When status === "none":  shows AP-contact form + an "Activate as Net 30"
 *   button. On click, POSTs the request and opens the pre-populated mailto:
 *   link so the admin can review/CC and send through Outlook.
 * - When status === "requested": yellow PENDING pill + "Resend email" button.
 *   Approve / Decline buttons appear only for admins who can_approve.
 * - When status === "approved": green pill + approver + notes.
 * - When status === "declined": red pill + reason.
 *
 * Audit trail is always rendered when present.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Banknote,
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Net30Panel({ healthSystemId, healthSystemName, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    ap_contact_name: "",
    ap_contact_email: "",
    ap_contact_phone: "",
    legal_entity_name: "",
    tax_id: "",
    estimated_monthly_volume: "",
    additional_notes: "",
  });
  const [decisionNotes, setDecisionNotes] = useState("");

  const fetchState = async () => {
    if (!healthSystemId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/health-systems/${healthSystemId}/net30`,
        { headers }
      );
      setState(res.data);
      // Hydrate the form from any persisted fields
      setForm((prev) => ({
        ...prev,
        ap_contact_name: res.data.ap_contact_name || "",
        ap_contact_email: res.data.ap_contact_email || "",
        ap_contact_phone: res.data.ap_contact_phone || "",
        legal_entity_name: res.data.legal_entity_name || healthSystemName || "",
        tax_id: res.data.tax_id || "",
        estimated_monthly_volume: res.data.estimated_monthly_volume || "",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Net 30 status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSystemId]);

  const submitRequest = async () => {
    if (!form.ap_contact_email) {
      toast.error("AP contact email is required so Flash Funding can reach Accounts Payable");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/health-systems/${healthSystemId}/net30/request`,
        form,
        { headers }
      );
      // Open Outlook compose window (or default mail client)
      window.location.href = res.data.mailto_url;
      toast.success("Outlook is opening — review CC list and click Send.");
      await fetchState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit Net 30 request");
    } finally {
      setSubmitting(false);
    }
  };

  const resendEmail = async () => {
    // Same endpoint — re-opens the mailto with the latest data
    await submitRequest();
  };

  const activateManually = async () => {
    if (!state?.can_approve) {
      toast.error("You don't have permission to manually activate Net 30. Ask a super-admin.");
      return;
    }
    const notes = window.prompt(
      "Manual Net 30 activation — skips Flash Funding entirely.\n\n" +
      "Use this ONLY when the hospital is already a Net 30 customer of yours " +
      "and is just new to the platform.\n\n" +
      "Optional: add a note for the audit trail (e.g. \"Existing Net 30 customer since 2019\")",
      ""
    );
    if (notes === null) return; // user cancelled
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/admin/health-systems/${healthSystemId}/net30/activate-manual`,
        { notes: notes || "Existing Net 30 customer — manually activated." },
        { headers }
      );
      toast.success("Activated as Net 30 (no email sent to Flash Funding)");
      await fetchState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to manually activate");
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (action) => {
    if (!state?.can_approve) {
      toast.error("You don't have permission to approve / decline Net 30 requests.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/admin/health-systems/${healthSystemId}/net30/${action}`,
        { notes: decisionNotes },
        { headers }
      );
      toast.success(action === "approve" ? "Approved as Net 30 customer" : "Declined");
      setDecisionNotes("");
      await fetchState();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !state) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Net 30 status…
      </div>
    );
  }

  const status = state.status || "none";

  return (
    <div className="space-y-3" data-testid="net30-panel">
      {/* Status header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-emerald-600" />
          <span className="font-semibold text-slate-800">Net 30 Terms</span>
          {status === "none" && (
            <Badge variant="outline" className="text-slate-600">Not activated</Badge>
          )}
          {status === "requested" && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              <Clock className="w-3 h-3 mr-1" /> Pending Flash Funding approval
            </Badge>
          )}
          {status === "approved" && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Approved
            </Badge>
          )}
          {status === "declined" && (
            <Badge className="bg-rose-100 text-rose-800 border-rose-200">
              <XCircle className="w-3 h-3 mr-1" /> Declined
            </Badge>
          )}
        </div>
        {state.can_approve && status === "requested" && (
          <Badge variant="outline" className="text-violet-700 border-violet-300">
            <ShieldCheck className="w-3 h-3 mr-1" /> You can approve
          </Badge>
        )}
      </div>

      {/* === NONE: AP form + Activate button === */}
      {status === "none" && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="net30-request-form">
          <p className="text-xs text-slate-600 leading-snug">
            Activating Net 30 sends a credit-vetting request to Sterile Mate&apos;s factoring partner,
            <strong className="text-slate-800"> Flash Funding (Dulce@Flash-funding.com)</strong>.
            We&apos;ll fill in everything below and open Outlook so you can review &amp; CC before sending.
            Terms will show as <em>pending</em> until a super-admin (or admin with the approve_net30 permission) approves.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Legal entity name</Label>
              <Input value={form.legal_entity_name} onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })} placeholder="e.g. Harris County Hospital District" data-testid="net30-legal-entity" />
            </div>
            <div>
              <Label className="text-xs">Tax ID / EIN</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="74-1234567" data-testid="net30-tax-id" />
            </div>
            <div>
              <Label className="text-xs">AP contact name</Label>
              <Input value={form.ap_contact_name} onChange={(e) => setForm({ ...form, ap_contact_name: e.target.value })} placeholder="Jane Smith" data-testid="net30-ap-name" />
            </div>
            <div>
              <Label className="text-xs">AP contact email *</Label>
              <Input type="email" value={form.ap_contact_email} onChange={(e) => setForm({ ...form, ap_contact_email: e.target.value })} placeholder="ap@hospital.org" data-testid="net30-ap-email" />
            </div>
            <div>
              <Label className="text-xs">AP contact phone</Label>
              <Input value={form.ap_contact_phone} onChange={(e) => setForm({ ...form, ap_contact_phone: e.target.value })} placeholder="(713) 555-1212" data-testid="net30-ap-phone" />
            </div>
            <div>
              <Label className="text-xs">Est. monthly PO volume</Label>
              <Input value={form.estimated_monthly_volume} onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value })} placeholder="$50k - $100k" data-testid="net30-volume" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Additional notes (optional)</Label>
            <Textarea rows={2} value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} placeholder="Long-time relationship, high priority…" data-testid="net30-notes" />
          </div>
          <Button
            onClick={submitRequest}
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="net30-activate-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Activate as Net 30 Customer
          </Button>
          <p className="text-[11px] text-slate-500 italic text-center">
            Opens Outlook compose to <strong>Dulce@Flash-funding.com</strong> · CCs you
          </p>

          {/* Manual activation (skip Flash Funding) — for existing Net 30 customers */}
          <div className="border-t border-slate-200 pt-3 mt-2">
            <p className="text-[11px] text-slate-500 mb-2 leading-snug">
              <strong className="text-slate-700">Already a Net 30 customer?</strong>{" "}
              If this hospital is just new to the platform but is already on Net 30 terms with you,
              skip the Flash Funding vetting and activate directly.
            </p>
            <Button
              onClick={activateManually}
              disabled={submitting || !state?.can_approve}
              variant="outline"
              className="w-full border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="net30-manual-activate-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Manual Activate — No Flash Funding Email
            </Button>
            {!state?.can_approve && (
              <p className="text-[11px] text-slate-400 italic text-center mt-1">
                Requires super-admin or approve_net30 permission
              </p>
            )}
          </div>
        </div>
      )}

      {/* === REQUESTED: pending state === */}
      {status === "requested" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="net30-pending">
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">Vetting request sent</p>
            <p className="text-xs">
              Sent to <strong>Dulce@Flash-funding.com</strong> on{" "}
              {state.requested_at && new Date(state.requested_at).toLocaleString()}
              {" "}by <strong>{state.requested_by}</strong>.
            </p>
            <p className="text-xs mt-2 text-amber-800">
              Terms show as <em>pending</em> until Flash Funding clears the credit
              check and an authorized admin approves below.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={resendEmail} disabled={submitting} data-testid="net30-resend-btn">
              {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Resend email to Flash Funding
            </Button>
          </div>

          {state.can_approve && (
            <div className="border-t border-amber-200 pt-3 space-y-2">
              <Label className="text-xs font-semibold text-amber-900">Decision notes (visible in audit trail)</Label>
              <Textarea rows={2} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} placeholder="e.g. Flash approved $75k credit limit" data-testid="net30-decision-notes" />
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => decide("approve")} disabled={submitting} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="net30-approve-btn">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Approve Net 30
                </Button>
                <Button onClick={() => decide("decline")} disabled={submitting} size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="net30-decline-btn">
                  <XCircle className="w-3 h-3 mr-1" /> Decline
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === APPROVED === */}
      {status === "approved" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <p className="font-semibold">
            Approved {state.approved_at && new Date(state.approved_at).toLocaleDateString()}
            {state.audit_trail?.some?.(a => a.action === "manual_activated") && (
              <Badge variant="outline" className="ml-2 text-[10px] border-slate-300 text-slate-600 bg-white">
                Manual · No Flash Funding email
              </Badge>
            )}
          </p>
          <p>By <strong>{state.approved_by}</strong></p>
          {state.approval_notes && (
            <p className="mt-1 italic">&ldquo;{state.approval_notes}&rdquo;</p>
          )}
        </div>
      )}

      {/* === DECLINED === */}
      {status === "declined" && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
          <p className="font-semibold">
            Declined {state.declined_at && new Date(state.declined_at).toLocaleDateString()}
          </p>
          {state.decline_reason && (
            <p className="mt-1 italic">&ldquo;{state.decline_reason}&rdquo;</p>
          )}
          {state.can_approve && (
            <div className="mt-2">
              <Button onClick={() => decide("approve")} disabled={submitting} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="net30-override-approve">
                Override &amp; Approve
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Audit trail */}
      {Array.isArray(state.audit_trail) && state.audit_trail.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none">
            Audit trail ({state.audit_trail.length})
          </summary>
          <ul className="mt-2 space-y-1 pl-4 border-l-2 border-slate-200">
            {state.audit_trail.map((a, i) => (
              <li key={i} className="text-slate-600">
                <span className="font-mono text-[10px] text-slate-400">{a.at && new Date(a.at).toLocaleString()}</span>
                {" · "}
                <strong className="capitalize">{a.action}</strong>
                {" by "}<span className="text-slate-700">{a.by}</span>
                {a.details && <span className="text-slate-500"> — {a.details}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
